import React from 'react';
import { Box, Card, CardContent, Typography, alpha } from '@mui/material';
import { GridOn } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const RiskMatrix = ({ downRisk10, downRisk20, downRisk60, exposure, drawdown }) => {
  const { t } = useTranslation();
  const getRiskZone = (dr10, dr20, dr60) => {
    const avg = (dr10 + dr20 + dr60) / 3;
    if (avg < 0.3) return { labelKey: 'risk.lowRisk', color: '#10b981' };
    if (avg < 0.5) return { labelKey: 'risk.moderate', color: '#f59e0b' };
    if (avg < 0.7) return { labelKey: 'risk.highRisk', color: '#ef4444' };
    return { labelKey: 'risk.critical', color: '#991b1b' };
  };

  const riskZone = getRiskZone(downRisk10 || 0, downRisk20 || 0, downRisk60 || 0);

  const matrixData = [
    { label: 'DR10', value: downRisk10 || 0, weight: 0.3 },
    { label: 'DR20', value: downRisk20 || 0, weight: 0.4 },
    { label: 'DR60', value: downRisk60 || 0, weight: 0.3 },
  ];

  const exposureData = [
    { label: t('risk.exposure'), value: exposure || 0, target: 1.0 },
    { label: t('risk.drawdown'), value: Math.abs(drawdown || 0), target: 0.1 },
  ];

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        background: '#ffffff',
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Header - Ultra Compact */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(riskZone.color, 0.1),
              color: riskZone.color,
            }}
          >
            <GridOn sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.9375rem', lineHeight: 1.2, mb: 0.25 }}>
              {t('risk.riskMatrix')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', lineHeight: 1 }}>
              {t('risk.multiDimensional')}
            </Typography>
          </Box>
        </Box>

        {/* Risk Zone - Clean Text Style */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
            pb: 1.5,
            borderBottom: '1px solid',
            borderColor: alpha('#000', 0.06),
          }}
        >
          <Typography 
            variant="overline" 
            sx={{ 
              fontSize: '0.625rem',
              fontWeight: 600,
              letterSpacing: 0.5,
              color: 'text.secondary',
              lineHeight: 1,
            }}
          >
              {t('risk.currentZone')}
            </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: riskZone.color,
                boxShadow: `0 0 0 2px ${alpha(riskZone.color, 0.15)}`,
              }}
            />
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 700, 
                color: riskZone.color, 
                fontSize: '0.875rem',
                letterSpacing: 0.25,
              }}
            >
              {t(riskZone.labelKey)}
            </Typography>
          </Box>
        </Box>

        {/* Two Column Layout for Metrics */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
          {/* Left Column: DownRisk Components */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 700, mb: 1, lineHeight: 1 }}>
              DownRisk Components
            </Typography>
            {matrixData.map((item, idx) => (
              <Box key={idx} sx={{ mb: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.3 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.625rem', fontWeight: 600 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.625rem', fontWeight: 700, color: riskZone.color }}>
                    {(item.value * 100).toFixed(1)}%
                  </Typography>
                </Box>
                <Box
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    bgcolor: alpha('#000', 0.05),
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: `${item.value * 100}%`,
                      bgcolor: riskZone.color,
                      borderRadius: 2,
                      transition: 'width 0.5s ease',
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.5625rem', mt: 0.2, lineHeight: 1 }}>
                  {t('risk.weight')}: {(item.weight * 100).toFixed(0)}%
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Right Column: Portfolio Metrics */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 700, mb: 1, lineHeight: 1 }}>
              Portfolio Metrics
            </Typography>
            {exposureData.map((item, idx) => (
              <Box key={idx} sx={{ mb: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.3 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.625rem', fontWeight: 600 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.625rem', fontWeight: 700, color: '#3b82f6' }}>
                    {(item.value * 100).toFixed(1)}% / {(item.target * 100).toFixed(0)}%
                  </Typography>
                </Box>
                <Box
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    bgcolor: alpha('#000', 0.05),
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {/* Target line */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${item.target * 100}%`,
                      top: -1,
                      bottom: -1,
                      width: 1,
                      bgcolor: '#6b7280',
                      zIndex: 2,
                    }}
                  />
                  {/* Current value */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: `${Math.min(item.value * 100, 100)}%`,
                      bgcolor: item.value > item.target ? '#ef4444' : '#3b82f6',
                      borderRadius: 2,
                      transition: 'width 0.5s ease',
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.5625rem', mt: 0.2, lineHeight: 1 }}>
                  Target: {(item.target * 100).toFixed(0)}%
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RiskMatrix;
